// firebase.js - FIXED: Proper notification sync between SW and header
import { initializeApp } from "firebase/app";
import { getAnalytics } from "firebase/analytics";
import { getAuth, GoogleAuthProvider } from "firebase/auth";
import { getMessaging, getToken, onMessage } from "firebase/messaging";
import axios from "axios";

// Your web app's Firebase configuration
const firebaseConfig = {
  apiKey: "AIzaSyCvExWvoxDGrkua1HyEPrA5aImHZZZIGJw",
  authDomain: "moxydrivingschool.firebaseapp.com",
  projectId: "moxydrivingschool",
  storageBucket: "moxydrivingschool.firebasestorage.app",
  messagingSenderId: "94464463637",
  appId: "1:94464463637:web:ef3f7a99cdfd6fa3f1cf1c",
  measurementId: "G-WD9Z8SNSM6"
};

// VAPID Key for FCM
const VAPID_KEY = "BKbaI7FxxSU4D0cI0zcmxwzwhGsg4P5V8NOspsLFKWVmES1d1fgk4Z3zLC1WhMXRmWxsExOqAmyKCGWPXXikyHw";

// Initialize Firebase
const app = initializeApp(firebaseConfig);
export const provider = new GoogleAuthProvider();
export const analytics = getAnalytics(app);
export const auth = getAuth(app);
export const messaging = getMessaging(app);

// Export onMessage for use in components
export { onMessage };

// Request management to prevent duplicate requests
let isRequestPending = false;

// Notification listeners registry - ENHANCED
const notificationListeners = new Set();
const notificationUpdateListeners = new Set();

// Register notification listener - ENHANCED
export const addNotificationListener = (callback) => {
  notificationListeners.add(callback);
  return () => notificationListeners.delete(callback);
};

// Register notification update listener for bulk updates
export const addNotificationUpdateListener = (callback) => {
  notificationUpdateListeners.add(callback);
  return () => notificationUpdateListeners.delete(callback);
};

// Notify all listeners - ENHANCED
const notifyListeners = (notification) => {
  notificationListeners.forEach(callback => {
    try {
      callback(notification);
    } catch (error) {
      console.error('Error in notification listener:', error);
    }
  });
};

// Notify update listeners with all notifications
const notifyUpdateListeners = (notifications, unreadCount) => {
  notificationUpdateListeners.forEach(callback => {
    try {
      callback(notifications, unreadCount);
    } catch (error) {
      console.error('Error in notification update listener:', error);
    }
  });
};

// Check service worker registration
export const checkServiceWorkerRegistration = async () => {
  try {
    if (!('serviceWorker' in navigator)) {
      throw new Error('Service Worker not supported');
    }
    const registration = await navigator.serviceWorker.ready;
    return registration;
  } catch (error) {
    console.error("Service worker registration check failed:", error);
    return null;
  }
};

// Get FCM Token
export const getFCMToken = async () => {
  try {
    // Check if messaging is supported
    if (!('Notification' in window) || !('serviceWorker' in navigator) || !('PushManager' in window)) {
      console.warn('Push messaging is not supported');
      return null;
    }

    // Request notification permission first
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') {
      console.warn('Notification permission denied');
      return null;
    }

    // Wait for service worker to be ready
    const registration = await checkServiceWorkerRegistration();
    if (!registration) {
      console.error('Service worker not ready');
      return null;
    }

    const currentToken = await getToken(messaging, {
      serviceWorkerRegistration: registration,
      vapidKey: VAPID_KEY,
    });
    
    if (currentToken) {
      console.log('FCM Token retrieved:', currentToken);
      return currentToken;
    } else {
      console.log('No registration token available.');
      return null;
    }
  } catch (error) {
    console.error("Error retrieving FCM token:", error);
    return null;
  }
};

// FIXED: Enhanced foreground message listener with proper sync
export const setupForegroundMessageListener = () => {
  return onMessage(messaging, async (payload) => {
    console.log('Message received in foreground:', payload);
    
    const notification = formatNotificationData(payload);
    
    // Store notification in service worker
    await storeNotificationInServiceWorker(notification);
    
    // Get updated notifications from service worker
    const allNotifications = await syncNotificationsFromServiceWorker();
    const unreadCount = allNotifications.filter(n => !n.read).length;
    
    // Notify individual notification listeners
    notifyListeners(notification);
    
    // Notify bulk update listeners with all notifications
    notifyUpdateListeners(allNotifications, unreadCount);
    
    // Update app badge
    setAppBadge(unreadCount);
  });
};

// Function to set app badge count
export const setAppBadge = (count) => {
  if ('navigator' in window && 'setAppBadge' in navigator) {
    navigator.setAppBadge(count).catch((error) => {
      console.error('Error setting app badge:', error);
    });
  }
  
  // Send message to service worker
  if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
    navigator.serviceWorker.controller.postMessage({
      type: 'UPDATE_BADGE',
      count: count
    });
  }
};

// Function to clear app badge
export const clearAppBadge = () => {
  if ('navigator' in window && 'clearAppBadge' in navigator) {
    navigator.clearAppBadge().catch((error) => {
      console.error('Error clearing app badge:', error);
    });
  }
  
  // Send message to service worker
  if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
    navigator.serviceWorker.controller.postMessage({
      type: 'CLEAR_BADGE'
    });
  }
};

// Function to check if notifications are supported
export const isNotificationSupported = () => {
  return 'Notification' in window && 'serviceWorker' in navigator && 'PushManager' in window;
};

// Function to check current notification permission
export const getNotificationPermission = () => {
  if ('Notification' in window) {
    return Notification.permission;
  }
  return 'unsupported';
};

// ENHANCED: Service worker message listener with comprehensive handling
export const setupServiceWorkerMessageListener = () => {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.addEventListener('message', async (event) => {
      console.log('Message from service worker:', event.data);
      
      switch (event.data.type) {
        case 'NOTIFICATION_CLICK':
          if (event.data.url && window.location.pathname !== event.data.url) {
            window.location.href = event.data.url;
          }
          break;
          
        case 'NEW_NOTIFICATION':
          // Handle new notifications from service worker
          const notification = event.data.notification;
          notifyListeners(notification);
          
          // Also get full updated list and notify update listeners
          try {
            const allNotifications = await syncNotificationsFromServiceWorker();
            const unreadCount = allNotifications.filter(n => !n.read).length;
            notifyUpdateListeners(allNotifications, unreadCount);
          } catch (error) {
            console.error('Error syncing after new notification:', error);
          }
          break;
          
        case 'NOTIFICATIONS_UPDATED':
          // Handle bulk notification updates
          const { notifications, unreadCount } = event.data;
          notifyUpdateListeners(notifications || [], unreadCount || 0);
          break;
          
        case 'NOTIFICATIONS_RESPONSE':
        case 'NOTIFICATIONS_CLEARED':
          // These are handled by specific functions
          break;
          
        default:
          console.log('Unknown message from service worker:', event.data);
      }
    });
  }
};

// Function to register service worker
export const registerServiceWorker = async () => {
  if ('serviceWorker' in navigator) {
    try {
      const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
      console.log('Service Worker registered successfully:', registration);
      
      // Setup message listener
      setupServiceWorkerMessageListener();
      
      return registration;
    } catch (error) {
      console.error('Service Worker registration failed:', error);
      return null;
    }
  } else {
    console.log('Service Workers not supported');
    return null;
  }
};

// Utility function to format notification data
export const formatNotificationData = (payload) => {
  return {
    id: payload.messageId || payload.data?.gcm_message_id || Date.now() + Math.random(),
    title: payload.notification?.title || payload.data?.title || 'New Notification',
    body: payload.notification?.body || payload.data?.body || 'You have a new message',
    icon: payload.notification?.icon || payload.data?.icon_url || '/android-chrome-192x192.webp',
    image: payload.notification?.image || payload.data?.image,
    type: payload.data?.type || 'info',
    url: payload.data?.click_action || payload.data?.url,
    timestamp: Date.now(),
    data: payload.data || {},
    read: false
  };
};

// Function to test notification - ENHANCED
export const testNotification = async () => {
  const testPayload = {
    messageId: 'test_' + Date.now(),
    notification: {
      title: 'Test Notification',
      body: 'This is a test notification to verify the system is working.'
    },
    data: {
      type: 'info',
      click_action: '/test'
    }
  };
  
  const notification = formatNotificationData(testPayload);
  
  // Store in service worker
  await storeNotificationInServiceWorker(notification);
  
  // Get updated notifications and notify listeners
  const allNotifications = await syncNotificationsFromServiceWorker();
  const unreadCount = allNotifications.filter(n => !n.read).length;
  
  // Notify listeners
  notifyListeners(notification);
  notifyUpdateListeners(allNotifications, unreadCount);
  
  // Update badge
  setAppBadge(unreadCount);
  
  return notification;
};

// Validate FCM token by attempting to get a fresh one and comparing
const validateFCMToken = async (existingToken) => {
  try {
    // Check if messaging is supported
    if (!isNotificationSupported()) {
      return false;
    }

    // Check notification permission
    if (Notification.permission !== 'granted') {
      return false;
    }

    // Try to get current token
    const registration = await checkServiceWorkerRegistration();
    if (!registration) {
      return false;
    }

    const currentToken = await getToken(messaging, {
      serviceWorkerRegistration: registration,
      vapidKey: VAPID_KEY,
    });
    
    // If we can't get a token or it's different, existing token is invalid
    return currentToken === existingToken;
    
  } catch (error) {
    console.error('Error validating FCM token:', error);
    return false;
  }
};

// Enhanced token registration with retry logic
export const requestNotificationPermissionAndToken = async (retryCount = 0) => {
  const MAX_RETRIES = 3;
  
  try {
    const permission = await Notification.requestPermission();
    
    if (permission !== 'granted') {
      return { 
        success: false, 
        error: 'Permission denied', 
        permission 
      };
    }
    
    const token = await getFCMToken();
    
    if (!token) {
      if (retryCount < MAX_RETRIES) {
        console.log(`Retrying token generation (attempt ${retryCount + 1})`);
        await new Promise(resolve => setTimeout(resolve, 1000 * (retryCount + 1)));
        return requestNotificationPermissionAndToken(retryCount + 1);
      }
      return { 
        success: false, 
        error: 'No token available after retries', 
        permission 
      };
    }
    
    // Send token to backend with retry logic
    const backendResult = await sendFCMTokentoBackendWithRetry(token);
    
    if (backendResult.resultCode === 0) {
      return { success: true, token, permission };
    } else {
      return { 
        success: false, 
        error: backendResult.error || 'Backend registration failed', 
        permission 
      };
    }
    
  } catch (error) {
    console.error('Error in enhanced token request:', error);
    return { success: false, error: error.message, permission: 'error' };
  }
};

// Backend token registration with retry logic
const sendFCMTokentoBackendWithRetry = async (token, deviceName = null, retryCount = 0) => {
  const MAX_RETRIES = 3;
  
  try {
    const result = await sendFCMTokentoBackend(token, deviceName);
    
    // If token registration failed due to server error, retry
    if (result.resultCode !== 0 && retryCount < MAX_RETRIES) {
      console.log(`Retrying backend registration (attempt ${retryCount + 1})`);
      await new Promise(resolve => setTimeout(resolve, 1000 * (retryCount + 1)));
      return sendFCMTokentoBackendWithRetry(token, deviceName, retryCount + 1);
    }
    
    return result;
    
  } catch (error) {
    if (retryCount < MAX_RETRIES) {
      console.log(`Retrying backend registration due to error (attempt ${retryCount + 1})`);
      await new Promise(resolve => setTimeout(resolve, 1000 * (retryCount + 1)));
      return sendFCMTokentoBackendWithRetry(token, deviceName, retryCount + 1);
    }
    throw error;
  }
};

// ENHANCED: Firebase messaging initialization with proper sync
export const initializeFirebaseMessaging = async () => {
  try {
    // Step 1: Register service worker first
    await registerServiceWorker();
    
    // Step 2: Setup foreground message listener (FIXED)
    setupForegroundMessageListener();
    
    // Step 3: Check if we have a valid backend token first
    const backendToken = await getServerToken();
    let shouldRegisterNewToken = false;
    
    if (backendToken?.registration_id) {
      // Step 4: Validate existing token
      const isValid = await validateFCMToken(backendToken.registration_id);
      if (!isValid) {
        console.log('Backend token is invalid, will register new token');
        shouldRegisterNewToken = true;
      } else {
        console.log('Using existing valid backend token');
      }
    } else {
      console.log('No backend token found, will register new token');
      shouldRegisterNewToken = true;
    }
    
    // Step 5: Get new token if needed
    if (shouldRegisterNewToken) {
      const result = await requestNotificationPermissionAndToken();
      return result;
    }
    
    return { 
      success: true, 
      token: backendToken.registration_id, 
      permission: Notification.permission,
      message: 'Using existing token'
    };
    
  } catch (error) {
    console.error('Error initializing Firebase messaging:', error);
    return { success: false, error: error.message };
  }
};

// Periodic token refresh (call this every few hours or on app focus)
export const refreshFCMTokenIfNeeded = async () => {
  try {
    // Check if stored token is still valid
    const backendToken = await getServerToken();
    
    if (!backendToken?.registration_id) {
      console.log('No backend token found, initializing FCM');
      return await initializeFirebaseMessaging();
    }
    
    const isValid = await validateFCMToken(backendToken.registration_id);
    
    if (!isValid) {
      console.log('Backend token is invalid, refreshing');
      return await initializeFirebaseMessaging();
    }
    
    return { success: true, token: backendToken.registration_id, message: 'Token is valid' };
    
  } catch (error) {
    console.error('Error refreshing FCM token:', error);
    return { success: false, error: error.message };
  }
};

// ENHANCED: Main app initialization with proper sync
export const initializeAppWithFCM = async () => {
  try {
    // Check if user is authenticated
    const authToken = localStorage.getItem("backend_token");
    if (!authToken) {
      console.log('User not authenticated, skipping FCM initialization');
      return { success: false, error: 'User not authenticated' };
    }
    
    // Register service worker first
    const swRegistration = await registerServiceWorker();
    if (!swRegistration) {
      return { success: false, error: 'Service worker registration failed' };
    }

    // Initialize FCM
    const result = await initializeFirebaseMessaging();
    
    if (result.success) {
      console.log('FCM initialized successfully');
      
      // IMPORTANT: Sync existing notifications from service worker immediately
      await syncNotificationsFromServiceWorker();
      
      // Setup periodic refresh (every 4 hours)
      setInterval(refreshFCMTokenIfNeeded, 4 * 60 * 60 * 1000);
      
      // Refresh on app focus/visibility change
      document.addEventListener('visibilitychange', () => {
        if (!document.hidden) {
          refreshFCMTokenIfNeeded();
          syncNotificationsFromServiceWorker();
        }
      });
      
      return { success: true, token: result.token };
    } else {
      console.warn('FCM initialization failed:', result.error);
      return result;
    }
    
  } catch (error) {
    console.error('Error in FCM initialization:', error);
    return { success: false, error: error.message };
  }
};

// ENHANCED: Sync notifications from service worker with proper listener notification
export const syncNotificationsFromServiceWorker = () => {
  return new Promise((resolve, reject) => {
    if (!('serviceWorker' in navigator) || !navigator.serviceWorker.controller) {
      resolve([]);
      return;
    }

    const messageChannel = new MessageChannel();
    
    messageChannel.port1.onmessage = (event) => {
      const { type, notifications, error } = event.data;
      
      if (type === 'NOTIFICATIONS_RESPONSE') {
        const allNotifications = notifications || [];
        const unreadCount = allNotifications.filter(n => !n.read).length;
        
        // Notify update listeners with the synced notifications
        notifyUpdateListeners(allNotifications, unreadCount);
        
        resolve(allNotifications);
      } else if (type === 'ERROR') {
        reject(new Error(error));
      } else {
        resolve([]);
      }
    };

    navigator.serviceWorker.controller.postMessage(
      { type: 'GET_NOTIFICATIONS' },
      [messageChannel.port2]
    );

    // Timeout after 5 seconds
    setTimeout(() => {
      resolve([]);
    }, 5000);
  });
};

// ENHANCED: Setup comprehensive notification listener
export const setupEnhancedNotificationListener = (callback) => {
  // Register for individual notifications
  const unsubscribeIndividual = addNotificationListener(callback);
  
  // Register for bulk updates (this is what the header needs)
  const unsubscribeBulk = addNotificationUpdateListener(callback);
  
  // Regular foreground listener
  const unsubscribeForeground = setupForegroundMessageListener();
  
  // Periodic sync with service worker (reduced frequency)
  const syncInterval = setInterval(async () => {
    try {
      await syncNotificationsFromServiceWorker();
    } catch (error) {
      console.error('Error in periodic sync:', error);
    }
  }, 60000); // Every 60 seconds

  // Return cleanup function
  return () => {
    if (unsubscribeIndividual) unsubscribeIndividual();
    if (unsubscribeBulk) unsubscribeBulk();
    if (unsubscribeForeground) unsubscribeForeground();
    clearInterval(syncInterval);
  };
};

// ENHANCED: Store notification in service worker with promise
const storeNotificationInServiceWorker = (notification) => {
  return new Promise((resolve) => {
    if (!('serviceWorker' in navigator) || !navigator.serviceWorker.controller) {
      resolve();
      return;
    }

    const messageChannel = new MessageChannel();
    
    messageChannel.port1.onmessage = (event) => {
      resolve(event.data);
    };
    
    navigator.serviceWorker.controller.postMessage(
      { 
        type: 'ADD_NOTIFICATION',
        notification: notification
      },
      [messageChannel.port2]
    );

    // Timeout
    setTimeout(() => resolve(), 3000);
  });
};

// Mark notifications as read in service worker
export const markNotificationsAsReadInServiceWorker = (notificationIds = []) => {
  return new Promise((resolve) => {
    if (!('serviceWorker' in navigator) || !navigator.serviceWorker.controller) {
      resolve();
      return;
    }

    const messageChannel = new MessageChannel();
    
    messageChannel.port1.onmessage = (event) => {
      // After marking as read, sync notifications to update listeners
      setTimeout(async () => {
        try {
          await syncNotificationsFromServiceWorker();
        } catch (error) {
          console.error('Error syncing after mark as read:', error);
        }
      }, 100);
      
      resolve(event.data);
    };

    navigator.serviceWorker.controller.postMessage(
      { 
        type: 'MARK_NOTIFICATIONS_READ',
        ids: notificationIds 
      },
      [messageChannel.port2]
    );

    // Timeout
    setTimeout(() => resolve(), 3000);
  });
};

// Clear notifications in service worker
export const clearNotificationsInServiceWorker = (notificationIds = []) => {
  return new Promise((resolve) => {
    if (!('serviceWorker' in navigator) || !navigator.serviceWorker.controller) {
      resolve();
      return;
    }

    const messageChannel = new MessageChannel();
    
    messageChannel.port1.onmessage = (event) => {
      // After clearing, sync notifications to update listeners
      setTimeout(async () => {
        try {
          await syncNotificationsFromServiceWorker();
        } catch (error) {
          console.error('Error syncing after clear:', error);
        }
      }, 100);
      
      resolve(event.data);
    };

    navigator.serviceWorker.controller.postMessage(
      { 
        type: 'CLEAR_NOTIFICATIONS',
        ids: notificationIds 
      },
      [messageChannel.port2]
    );

    // Timeout
    setTimeout(() => resolve(), 3000);
  });
};

// Send FCM Token to Backend
export const sendFCMTokentoBackend = async (theToken, deviceName = null) => {
  if (isRequestPending) {
    console.log("Token request already in progress. Skipping.");
    return { resultCode: 0, message: "Request already pending" };
  }
  
  isRequestPending = true;

  const body = { 
    fcm_token: theToken,
    device_name: deviceName || navigator.userAgent.substring(0, 50)
  };

  try {
    const response = await axios.post(
      `${process.env.REACT_APP_API_URL}/api/fcm_register`,
      body,
      {
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${localStorage.getItem("backend_token")}`,
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error sending FCM token to backend:", error);
    return { 
      resultCode: 1, 
      error: error.response?.data?.error || error.message 
    };
  } finally {
    isRequestPending = false;
  }
};

// Get Server Token
export const getServerToken = async () => {
  const url = `${process.env.REACT_APP_API_URL}/api/get_fcm_token`;
  
  try {
    const response = await axios.get(url, {
      headers: {
        Accept: "application/json",
        Authorization: `Bearer ${localStorage.getItem("backend_token")}`,
      },
    });
    
    const data = response.data;
    console.log({ fcm_token: data?.registration_id });
    return data;
  } catch (error) {
    console.error("Error getting server token:", error);
    return null;
  }
};

// Get all FCM devices for user
export const getUserFCMDevices = async () => {
  const url = `${process.env.REACT_APP_API_URL}/api/fcm_devices`;
  
  try {
    const response = await axios.get(url, {
      headers: {
        Accept: "application/json",
        Authorization: `Bearer ${localStorage.getItem("backend_token")}`,
      },
    });
    
    return response.data;
  } catch (error) {
    console.error("Error getting FCM devices:", error);
    return { resultCode: 1, error: error.message };
  }
};

// Update FCM device status
export const updateFCMDeviceStatus = async (fcmToken, active, deviceName = null) => {
  const url = `${process.env.REACT_APP_API_URL}/api/fcm_update`;
  
  const body = {
    fcm_token: fcmToken,
    active: active
  };
  
  if (deviceName) {
    body.device_name = deviceName;
  }
  
  try {
    const response = await axios.patch(url, body, {
      headers: {
        Accept: "application/json",
        Authorization: `Bearer ${localStorage.getItem("backend_token")}`,
      },
    });
    
    return response.data;
  } catch (error) {
    console.error("Error updating FCM device status:", error);
    return { resultCode: 1, error: error.message };
  }
};

// Remove FCM device
export const removeFCMDevice = async (fcmToken) => {
  const url = `${process.env.REACT_APP_API_URL}/api/fcm_remove`;
  
  try {
    const response = await axios.delete(url, {
      headers: {
        Accept: "application/json",
        Authorization: `Bearer ${localStorage.getItem("backend_token")}`,
      },
      data: {
        fcm_token: fcmToken
      }
    });
    
    return response.data;
  } catch (error) {
    console.error("Error removing FCM device:", error);
    return { resultCode: 1, error: error.message };
  }
};

// Cleanup old FCM devices
export const cleanupOldFCMDevices = async (keepCount = 1) => {
  const url = `${process.env.REACT_APP_API_URL}/api/fcm_cleanup`;
  
  try {
    const response = await axios.post(url, {
      keep_count: keepCount
    }, {
      headers: {
        Accept: "application/json",
        Authorization: `Bearer ${localStorage.getItem("backend_token")}`,
      },
    });
    
    return response.data;
  } catch (error) {
    console.error("Error cleaning up FCM devices:", error);
    return { resultCode: 1, error: error.message };
  }
};

// Legacy function aliases for backward compatibility
export const initializeFirebaseMessagingEnhanced = initializeFirebaseMessaging;
export const requestNotificationPermissionAndTokenEnhanced = requestNotificationPermissionAndToken;
export const initializeAppWithFCMEnhanced = initializeAppWithFCM;